<template>
  <TheModal :show="show" size="xl" :close-disabled="aiLoading || integrationLoading" @close="onClose">
    <div class="setup-onboarding quiz-modal">
      <div v-if="aiLoading || integrationLoading" class="quiz-loading-overlay">
        <div class="spinner-border text-primary mb-2" role="status" />
        <div class="fw-semibold">{{ loadingTitle }}</div>
        <div v-if="aiLoading" class="small text-muted mt-1">
          Обычно 10–30 секунд, иногда до 2–3 минут. Не закрывайте окно.
        </div>
        <div v-if="aiLoading && quizElapsedSeconds > 0" class="small text-muted mt-1">
          Прошло {{ quizElapsedSeconds }} сек.
        </div>
        <div v-if="quizSaveTotal > 0" class="small text-muted mt-1">
          Сохранено {{ quizSaveDone }} из {{ quizSaveTotal }}
        </div>
      </div>

      <template v-if="step === 'guide' && !success">
        <article v-if="setupIntro" class="product-guide-inline">
          <h2 class="h5 mb-2">{{ setupIntro.title }}</h2>
          <p v-if="setupIntro.lead" class="text-muted small mb-3">{{ setupIntro.lead }}</p>
          <ol class="product-guide-inline__steps mb-4">
            <li v-for="(item, index) in setupIntro.steps" :key="index" class="mb-3">
              <h3 class="h6 mb-1">{{ item.title }}</h3>
              <p class="text-muted small mb-0">{{ item.text }}</p>
            </li>
          </ol>
          <div class="d-flex flex-wrap gap-2 justify-content-end">
            <button type="button" class="btn btn-outline-secondary" @click="skipGuide">
              Пропустить обучение
            </button>
            <button type="button" class="btn btn-primary" @click="continueFromGuide">
              {{ setupIntro.ctaLabel || 'Далее к настройке' }}
            </button>
          </div>
        </article>
      </template>

      <template v-else-if="step === 'integration' && !success">
        <p class="mb-2">
          Если у вас есть аккаунт <strong>Tenderplan</strong>, укажите API-ключ — мы загрузим ваши поисковые ключи.
        </p>
        <p class="text-muted small mb-3">
          Ключ можно найти в личном кабинете Tenderplan в разделе настроек API.
          Без ключа поиск работает в стандартном режиме сервиса.
        </p>
        <div v-if="integrationError" class="alert alert-danger py-2 small">{{ integrationError }}</div>
        <div v-if="integrationSuccess" class="alert alert-success py-2 small">{{ integrationSuccess }}</div>
        <label class="form-label fw-semibold" for="ob-tp-token">API-ключ Tenderplan</label>
        <input
          id="ob-tp-token"
          v-model="integrationToken"
          type="password"
          class="form-control mb-3"
          autocomplete="off"
          placeholder="Вставьте ключ"
          :disabled="integrationLoading"
        />
        <div class="d-flex flex-wrap gap-2 justify-content-end">
          <button type="button" class="btn btn-outline-secondary" :disabled="integrationLoading" @click="skipIntegration">
            Пропустить
          </button>
          <button type="button" class="btn btn-primary" :disabled="integrationLoading || !integrationToken.trim()" @click="connectIntegration">
            <span v-if="integrationLoading" class="spinner-border spinner-border-sm me-1" role="status" />
            Подключить и загрузить ключи
          </button>
        </div>
      </template>

      <template v-else-if="step === 'quiz' && !success">
        <p class="mb-2">
          Для поиска закупок и AI-анализа нужны <strong>критерии победы</strong> и <strong>персональный поисковый ключ</strong>.
        </p>
        <p class="text-muted small mb-3">
          Ответьте на вопросы — ИИ сформирует критерии и подберёт ключевые слова. Параметры поиска (регионы, типы закупок, цены) задаёте вручную.
        </p>
        <div v-if="quizError" class="alert alert-danger py-2 small">{{ quizError }}</div>
        <div class="quiz-scroll vstack gap-4 mb-3">
          <section v-if="!skipCriteriaStep">
            <h6 class="fw-semibold mb-2">Критерии победы</h6>
            <div v-for="item in ONBOARDING_QUIZ_ITEMS" :key="item.id" class="quiz-item mb-3">
              <label class="form-label fw-semibold mb-1" :for="'ob-q-' + item.id">{{ item.question }}</label>
              <textarea
                :id="'ob-q-' + item.id"
                v-model="quizDraft[item.id]"
                class="form-control"
                rows="3"
                :placeholder="item.placeholder"
                :disabled="aiLoading"
              />
            </div>
          </section>

          <section>
            <h6 class="fw-semibold mb-2">Поисковый ключ</h6>
            <div v-for="item in ONBOARDING_KEY_QUIZ_ITEMS" :key="item.id" class="quiz-item mb-3">
              <label class="form-label fw-semibold mb-1" :for="'ob-q-' + item.id">{{ item.question }}</label>
              <textarea
                :id="'ob-q-' + item.id"
                v-model="quizDraft[item.id]"
                class="form-control"
                rows="3"
                :placeholder="item.placeholder"
                :disabled="aiLoading"
              />
            </div>
          </section>

          <section>
            <h6 class="fw-semibold mb-2">Параметры поиска</h6>
            <KeyFilterFields v-model="keyForm" id-prefix="ob-" :show-kz="false" :disabled="aiLoading" />
          </section>
        </div>
        <div class="d-flex flex-wrap gap-2 justify-content-end">
          <button type="button" class="btn btn-outline-secondary" :disabled="aiLoading" @click="skip">Пропустить</button>
          <button type="button" class="btn btn-outline-primary" :disabled="aiLoading" @click="fillManual">Вручную</button>
          <button type="button" class="btn btn-primary" :disabled="aiLoading" @click="submitQuiz">
            <span v-if="aiLoading" class="spinner-border spinner-border-sm me-1" role="status" />
            Сохранить
          </button>
        </div>
      </template>

      <template v-else-if="success">
        <div class="text-center py-3">
          <i class="fa-solid fa-circle-check text-success fa-3x mb-3" aria-hidden="true" />
          <h5 class="mb-2">Готово</h5>
          <p class="text-muted small mb-4">Настройка профиля завершена.</p>
          <button type="button" class="btn btn-primary" @click="finishDone">Перейти к поиску</button>
        </div>
      </template>
    </div>
  </TheModal>
</template>

<script setup>
import { ref, reactive, computed, watch, onUnmounted } from 'vue'
import TheModal from './TheModal.vue'
import KeyFilterFields from '@/components/key/KeyFilterFields.vue'
import api from '@/api'
import { invalidateWinCriteriaCache } from '@/utils/winCriteriaGate'
import { emitSetupCompleted } from '@/utils/setupGate'
import { syncUserKeys, applyKeySyncToStorage } from '@/utils/keySync'
import { connectTenderplan, integrationErrorMessage } from '@/utils/tenderplanIntegration'
import { createBlankKeyForm, buildKeyPayloadFromForm } from '@/composables/buildKeyPayload'
import {
  ONBOARDING_QUIZ_ITEMS,
  ONBOARDING_KEY_QUIZ_ITEMS,
  ONBOARDING_ALL_QUIZ_ITEMS,
} from '@/constants/setupQuiz'
import { getProductGuide } from '@/constants/productGuides'
import { hasSeenGuide, markGuideSeen } from '@/utils/productGuideStorage'
import {
  collectQuizAnswers,
  validateQuizRequired,
  fetchSetupFromQuiz,
  saveCriteriaFromAiList,
  saveKeyFromAiResult,
  quizErrorMessage,
} from '@/composables/useSetupOnboardingQuiz'

const props = defineProps({
  show: { type: Boolean, default: false },
  userId: { type: [String, Number], default: null },
  primaryKey: { type: Object, default: null },
  skipCriteriaStep: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'skip', 'manual', 'completed', 'integration-connected'])

const step = ref('integration')
const success = ref(false)
const quizError = ref('')
const integrationError = ref('')
const integrationSuccess = ref('')
const integrationToken = ref('')
const integrationLoading = ref(false)
const aiLoading = ref(false)
const quizLoadPhase = ref('idle')
const quizSaveDone = ref(0)
const quizSaveTotal = ref(0)
const quizElapsedSeconds = ref(0)
let quizElapsedTimer = null

function clearQuizElapsedTimer() {
  if (quizElapsedTimer) {
    clearInterval(quizElapsedTimer)
    quizElapsedTimer = null
  }
  quizElapsedSeconds.value = 0
}

watch(aiLoading, (loading) => {
  clearQuizElapsedTimer()
  if (!loading) return
  quizElapsedSeconds.value = 0
  quizElapsedTimer = setInterval(() => {
    quizElapsedSeconds.value += 1
  }, 1000)
})

onUnmounted(() => {
  clearQuizElapsedTimer()
})

const quizDraft = reactive({})
const keyForm = reactive(createBlankKeyForm())

const activeQuizItems = computed(() =>
  props.skipCriteriaStep ? ONBOARDING_KEY_QUIZ_ITEMS : ONBOARDING_ALL_QUIZ_ITEMS,
)

const setupIntro = computed(() => getProductGuide('setup-intro'))

const modalTitle = computed(() => {
  if (success.value) return 'Настройка профиля'
  if (step.value === 'guide') return setupIntro.value?.title || 'Добро пожаловать'
  if (step.value === 'integration') return 'Интеграция с Tenderplan'
  return 'Настройка профиля'
})

const loadingTitle = computed(() => {
  if (integrationLoading.value) return 'Подключаем Tenderplan…'
  if (quizLoadPhase.value === 'saving-criteria') return 'Сохраняем критерии…'
  if (quizLoadPhase.value === 'saving-key') return 'Создаём персональный ключ…'
  return 'ИИ формирует критерии и ключ…'
})

function resetDrafts() {
  for (const item of ONBOARDING_ALL_QUIZ_ITEMS) {
    quizDraft[item.id] = ''
  }
  const blank = createBlankKeyForm()
  Object.assign(keyForm, blank)
  Object.assign(keyForm.flags, blank.flags)
}

function initialOnboardingStep() {
  if (props.userId != null && hasSeenGuide(props.userId, 'setup-intro')) {
    return 'integration'
  }
  return 'guide'
}

watch(
  () => props.show,
  (visible) => {
    if (!visible) return
    quizError.value = ''
    integrationError.value = ''
    integrationSuccess.value = ''
    integrationToken.value = ''
    success.value = false
    step.value = initialOnboardingStep()
    resetDrafts()
  },
)

function markSetupIntroSeen() {
  if (props.userId != null) {
    markGuideSeen(props.userId, 'setup-intro')
  }
}

function skipGuide() {
  markSetupIntroSeen()
  step.value = 'integration'
}

function continueFromGuide() {
  markSetupIntroSeen()
  step.value = 'integration'
}

function onClose() {
  if (aiLoading.value || integrationLoading.value) return
  emit('close')
}

function skipIntegration() {
  if (integrationLoading.value) return
  step.value = 'quiz'
}

async function connectIntegration() {
  integrationError.value = ''
  integrationSuccess.value = ''
  const token = integrationToken.value.trim()
  if (!token) return

  integrationLoading.value = true
  try {
    const result = await connectTenderplan(api, token)
    const imported = result.imported ?? 0
    integrationSuccess.value =
      imported > 0
        ? `Подключено. Загружено поисковых ключей: ${imported}.`
        : 'Подключено. Поисковые ключи синхронизированы.'

    if (props.userId != null) {
      try {
        const syncResult = await syncUserKeys(api)
        applyKeySyncToStorage(props.userId, syncResult)
      } catch (_) {
        /* sync optional after connect */
      }
    }
    emit('integration-connected', result)
    step.value = 'quiz'
  } catch (e) {
    integrationError.value = integrationErrorMessage(e)
  } finally {
    integrationLoading.value = false
  }
}

function skip() {
  if (aiLoading.value) return
  emit('skip')
}

function fillManual() {
  if (aiLoading.value) return
  emit('manual')
}

async function submitQuiz() {
  quizError.value = ''
  const empty = validateQuizRequired(activeQuizItems.value, quizDraft)
  if (empty.length) {
    quizError.value = 'Ответьте на все обязательные вопросы.'
    return
  }

  const probe = buildKeyPayloadFromForm({ ...keyForm, name: 'Мой поиск', words: ['probe'] })
  if (probe.error) {
    quizError.value = probe.error
    return
  }

  aiLoading.value = true
  quizLoadPhase.value = 'generating'
  quizSaveDone.value = 0
  quizSaveTotal.value = 0
  try {
    const answers = collectQuizAnswers(activeQuizItems.value, quizDraft)
    const data = await fetchSetupFromQuiz(answers, {
      skipCriteria: props.skipCriteriaStep,
      skipKey: false,
    })

    if (!props.skipCriteriaStep) {
      const criteria = data.criteria
      if (Array.isArray(criteria) && criteria.length) {
        quizLoadPhase.value = 'saving-criteria'
        const critResult = await saveCriteriaFromAiList(criteria, {
          onSaveProgress: ({ done, total }) => {
            quizSaveDone.value = done
            quizSaveTotal.value = total
          },
        })
        if (!critResult.ok) {
          quizError.value = critResult.error
          return
        }
      }
    }

    quizLoadPhase.value = 'saving-key'
    quizSaveDone.value = 0
    quizSaveTotal.value = 0
    const keyResult = await saveKeyFromAiResult(data, props.primaryKey, keyForm)
    if (!keyResult.ok) {
      quizError.value = keyResult.error
      return
    }

    invalidateWinCriteriaCache()
    emitSetupCompleted()
    success.value = true
  } catch (e) {
    quizError.value = quizErrorMessage(e)
  } finally {
    aiLoading.value = false
    quizLoadPhase.value = 'idle'
    quizSaveDone.value = 0
    quizSaveTotal.value = 0
  }
}

function finishDone() {
  emit('completed')
}
</script>

<style scoped>
.quiz-scroll {
  max-height: min(70vh, 640px);
  overflow-y: auto;
}
.quiz-item textarea {
  resize: vertical;
}
.quiz-modal {
  position: relative;
  min-height: 120px;
}
.quiz-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 0.375rem;
}
.product-guide-inline__steps {
  list-style: none;
  padding: 0;
  margin: 0;
}
</style>
